import { useCallback, useEffect, useMemo, useState } from 'react'
import uiStates, {
  UiStateKey,
  UiStateScope,
} from '../../../../lib/commons/uiStates'
import { ROW_HEIGHT } from '../../../containers/BulkSheet'
import { ToolbarToggleValue } from '../../../components/toolbars/Toolbar/ToolbarToggle'
import { DateTerm, getFinancialYear } from '../../../../utils/date'
import store from '../../../../store'
import { WorkloadUnit } from '../../../../lib/functions/workload'
import {
  AggregateTarget,
  WbsItemType,
} from '../../../../domain/entity/WbsItemEntity'
import { AssignmentType, TimeGrain } from '../MemberWorkloadStatus'

export type StoredPageState = {
  aggregateTarget: AggregateTarget
  workloadUnit: WorkloadUnit
  timeGrain: TimeGrain
  assignmentType: AssignmentType
  fromDate: string
  toDate: string
  rowHeight: ROW_HEIGHT
  toolbar?: ToolbarToggleValue
}

export const usePageState = (
  functionUuid: string
): {
  aggregateTarget: AggregateTarget
  setAggregateTarget: (_: AggregateTarget) => void
  workloadUnit: WorkloadUnit
  setWorkloadUnit: (_: WorkloadUnit) => void
  timeGrain: TimeGrain
  setTimeGrain: (_: TimeGrain) => void
  assignmentType: AssignmentType
  setAssignmentType: (_: AssignmentType) => void
  fromDate: string
  setFromDate: (_: string) => void
  toDate: string
  setToDate: (_: string) => void
  rowHeight: ROW_HEIGHT
  setRowHeight: (_: ROW_HEIGHT) => void
  initialized: boolean
} => {
  const [aggregateTarget, setAggregateTarget] = useState<AggregateTarget>(
    WbsItemType.TASK
  )
  const [workloadUnit, setWorkloadUnit] = useState<WorkloadUnit>(
    WorkloadUnit.HOUR
  )
  const [timeGrain, setTimeGrain] = useState<TimeGrain>(TimeGrain.MONTH)
  const [assignmentType, setAssignmentType] = useState<AssignmentType>(
    AssignmentType.RESPONSIBLE
  )
  const defaultDateTerm: DateTerm = useMemo(() => {
    const startMonthOfFinancialYear = getFinancialYear(
      store.getState()?.tenant.organization
    )
    return {
      startDate: startMonthOfFinancialYear.formatForApi(),
      endDate: startMonthOfFinancialYear.addYears(1).addDays(-1).formatForApi(),
    }
  }, [])
  const [fromDate, setFromDate] = useState<string>(defaultDateTerm.startDate!)
  const [toDate, setToDate] = useState<string>(defaultDateTerm.endDate!)
  const [rowHeight, setRowHeight] = useState<number>(ROW_HEIGHT.SMALL)
  const [initialized, setInitialized] = useState<boolean>(false)

  const restorePageState = useCallback(async () => {
    const response = await uiStates.get({
      applicationFunctionUuid: functionUuid,
      key: UiStateKey.PageState,
      scope: UiStateScope.User,
    })
    const stored = response.json
    if (!stored.value) {
      setInitialized(true)
      return
    }
    const payload: StoredPageState = JSON.parse(stored.value)
    payload.aggregateTarget && setAggregateTarget(payload.aggregateTarget)
    payload.workloadUnit && setWorkloadUnit(payload.workloadUnit)
    payload.timeGrain && setTimeGrain(payload.timeGrain)
    payload.assignmentType && setAssignmentType(payload.assignmentType)
    payload.fromDate && setFromDate(payload.fromDate)
    payload.toDate && setToDate(payload.toDate)
    payload.rowHeight && setRowHeight(payload.rowHeight)
    setInitialized(true)
  }, [functionUuid])

  const rememberPageState = useCallback(
    (value: StoredPageState) => {
      uiStates.update(
        {
          key: UiStateKey.PageState,
          scope: UiStateScope.User,
          value: JSON.stringify(value),
        },
        functionUuid
      )
    },
    [functionUuid]
  )

  useEffect(() => {
    restorePageState()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!initialized) return
    rememberPageState({
      aggregateTarget,
      workloadUnit,
      timeGrain,
      assignmentType,
      fromDate,
      toDate,
      rowHeight,
    })
  }, [
    rememberPageState,
    aggregateTarget,
    workloadUnit,
    timeGrain,
    assignmentType,
    fromDate,
    toDate,
    rowHeight,
    initialized,
  ])

  return {
    aggregateTarget,
    setAggregateTarget,
    workloadUnit,
    setWorkloadUnit,
    timeGrain,
    setTimeGrain,
    assignmentType,
    setAssignmentType,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    rowHeight,
    setRowHeight,
    initialized,
  }
}
